import type { SpotlightResult } from '~~/shared/types/spotlight'

export function useSpotlight() {
  const auth = useAuth()
  const analytics = useAnalytics()
  const requestFetch = useRequestFetch() as typeof morningFetch
  const open = ref(false)
  const query = ref('')
  const results = ref<SpotlightResult[]>([])
  const loading = ref(false)
  const error = ref(false)
  let timer: ReturnType<typeof setTimeout> | undefined
  let timeout: ReturnType<typeof setTimeout> | undefined
  let controller: AbortController | undefined
  let generation = 0

  function reset() {
    generation++
    if (timer) clearTimeout(timer)
    if (timeout) clearTimeout(timeout)
    controller?.abort()
    controller = undefined
    query.value = ''
    results.value = []
    loading.value = false
    error.value = false
  }

  async function search() {
    if (!open.value || !auth.authenticated.value || !auth.tenant.value) return
    const current = ++generation
    controller?.abort()
    controller = new AbortController()
    const signal = controller.signal
    timeout = setTimeout(() => controller?.abort(), 5000)
    loading.value = true
    error.value = false
    try {
      const response = await requestFetch<{ results: SpotlightResult[]; hasMore: boolean }>(
        '/api/spotlight/search',
        { method: 'POST', body: { query: query.value, limit: 20 }, signal },
      )
      if (current !== generation || signal.aborted) return
      results.value = response.results
      if (query.value)
        analytics.trackEvent(response.results.length ? 'search_results' : 'search_no_result', {
          surface: 'spotlight',
          query_length: query.value.length,
          result_count: response.results.length,
        })
    } catch {
      if (current !== generation || signal.aborted) return
      results.value = []
      error.value = true
    } finally {
      if (timeout) clearTimeout(timeout)
      if (current === generation) loading.value = false
    }
  }

  watch(query, () => {
    if (timer) clearTimeout(timer)
    controller?.abort()
    if (!open.value) return
    timer = setTimeout(() => void search(), 200)
  })
  watch(
    () => [auth.tenant.value?.id, auth.identity.value?.id, auth.mode.value],
    () => reset(),
  )
  watch(open, (value) => {
    if (value) void search()
    else reset()
  })
  onBeforeUnmount(reset)

  return { open, query, results, loading, error, reset }
}
